<script setup>
import { ref, computed } from 'vue'
import { useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'

const props = defineProps({
  projet:           { type: Object,  default: null },
  type_bons:        { type: Array,   default: () => [] },
  type_bon_statuts: { type: Array,   default: () => [] },
  equipements:      { type: Array,   default: () => [] },
  type_fonctions:   { type: Array,   default: () => [] },
  date_elements:    { type: Boolean, default: false },
  can_see_costs:    { type: Boolean, default: false },
  defaut_statut_id: { type: String,  default: null },
  errors:           { type: Object,  default: () => ({}) },
  bons_url:         { type: String,  default: '/bons' },
})

// ── Formulaire principal ─────────────────────────────────────
const form = useForm({
  bon: {
    projet_id:    props.projet?.id ?? '',
    nom:          '',
    num_ts:       '',
    type_bon_id:  props.type_bons[0]?.id ?? '',
    debut:        '',
    chainage:     '',
    description:  '',
    commentaires: '',
    bon_statut_attributes: {
      type_bon_statut_id: props.defaut_statut_id ?? props.type_bon_statuts[0]?.id ?? '',
    },
    bon_equipements_attributes:   [],
    bon_type_fonctions_attributes: [],
    bon_items_attributes:         [],
  },
})

// ── Lignes dynamiques ────────────────────────────────────────
function emptyEquipement()   { return { equipement_id: '', quantite: 1, unite: 1, debut: '', fin: '', taux: '', _destroy: false } }
function emptyTypeFonction() { return { type_fonction_id: '', quantite: 1, unite: 1, debut: '', fin: '', taux: '', _destroy: false } }
function emptyItem()         { return { description: '', quantite: 1, unite: 1, taux: '', _destroy: false } }

function addEquipement()   { form.bon.bon_equipements_attributes.push(emptyEquipement()) }
function addTypeFonction() { form.bon.bon_type_fonctions_attributes.push(emptyTypeFonction()) }
function addItem()         { form.bon.bon_items_attributes.push(emptyItem()) }

function removeEquipement(i)   { form.bon.bon_equipements_attributes.splice(i, 1) }
function removeTypeFonction(i) { form.bon.bon_type_fonctions_attributes.splice(i, 1) }
function removeItem(i)         { form.bon.bon_items_attributes.splice(i, 1) }

// ── Images ───────────────────────────────────────────────────
const imageFiles   = ref([])
const imageInput   = ref(null)
const imagePreviews = ref([])

function onImageChange(e) {
  const files = Array.from(e.target.files)
  files.forEach(f => {
    imageFiles.value.push(f)
    imagePreviews.value.push({ name: f.name, src: URL.createObjectURL(f) })
  })
}
function removeImage(i) {
  imageFiles.value.splice(i, 1)
  imagePreviews.value.splice(i, 1)
}

// ── Soumission ───────────────────────────────────────────────
function submit() {
  form.transform(data => {
    const fd = { ...data.bon }
    return { bon: fd }
  }).post('/bons', {
    forceFormData: true,
    onBefore: () => {
      // inject images into form data manually
      if (imageFiles.value.length) {
        form.bon.images = imageFiles.value
      }
    },
  })
}

// ── Erreurs ──────────────────────────────────────────────────
const allErrors = computed(() => ({ ...form.errors, ...props.errors }))
function err(field) { return allErrors.value[field] }
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="flex items-center gap-3">
        <a :href="projet ? `/projets/${projet.id}` : bons_url"
           class="text-outline hover:text-on-surface transition-colors">
          <span class="material-symbols-outlined text-xl">arrow_back</span>
        </a>
        <span class="text-outline">/</span>
        <span class="text-sm font-black uppercase tracking-widest text-on-surface">Nouveau bon</span>
        <template v-if="projet">
          <span class="text-outline">/</span>
          <span class="font-mono text-sm font-bold text-primary">{{ projet.code }}</span>
          <span class="text-sm text-outline">{{ projet.nom }}</span>
        </template>
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-5xl mx-auto space-y-6">

        <!-- En-tête -->
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-black text-on-surface tracking-tight uppercase">Nouveau bon de travail</h1>
            <p v-if="projet" class="text-sm text-outline mt-1">
              Projet <span class="font-mono font-bold text-primary">{{ projet.code }}</span> — {{ projet.nom }}
            </p>
            <p v-else class="text-sm text-error mt-1 flex items-center gap-1">
              <span class="material-symbols-outlined text-sm">warning</span>
              Aucun projet sélectionné — accédez via la page d'un projet.
            </p>
          </div>
        </div>

        <!-- Erreurs globales -->
        <div v-if="Object.keys(allErrors).length > 0"
             class="bg-error/10 border border-error/30 rounded-xl px-5 py-4">
          <p class="text-xs font-black uppercase tracking-widest text-error mb-2">Erreurs de validation</p>
          <ul class="space-y-1">
            <li v-for="(msgs, field) in allErrors" :key="field"
                class="text-sm text-error">
              <span class="font-bold capitalize">{{ field }}</span> : {{ Array.isArray(msgs) ? msgs.join(', ') : msgs }}
            </li>
          </ul>
        </div>

        <!-- ── Section 1 : Informations générales ── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Informations générales</h2>
          </div>
          <div class="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">

            <!-- Nom -->
            <div class="sm:col-span-2">
              <label class="block text-[10px] font-black uppercase tracking-widest text-outline mb-1">Nom du bon <span class="text-error">*</span></label>
              <input v-model="form.bon.nom" type="text"
                     :class="err('nom') ? 'border-error/50 focus:ring-error/20' : 'border-outline-variant/20 focus:ring-primary/20'"
                     class="w-full bg-surface-container-highest border rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 transition-all" />
              <p v-if="err('nom')" class="text-xs text-error mt-1">{{ err('nom') }}</p>
            </div>

            <!-- Type bon -->
            <div>
              <label class="block text-[10px] font-black uppercase tracking-widest text-outline mb-1">Type de bon</label>
              <select v-model="form.bon.type_bon_id"
                      class="w-full bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all">
                <option v-for="t in type_bons" :key="t.id" :value="t.id">{{ t.description }}</option>
              </select>
            </div>

            <!-- Statut -->
            <div>
              <label class="block text-[10px] font-black uppercase tracking-widest text-outline mb-1">Statut</label>
              <select v-model="form.bon.bon_statut_attributes.type_bon_statut_id"
                      class="w-full bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all">
                <option v-for="s in type_bon_statuts" :key="s.id" :value="s.id">{{ s.description }}</option>
              </select>
            </div>

            <!-- Nº TS -->
            <div>
              <label class="block text-[10px] font-black uppercase tracking-widest text-outline mb-1">Nº TS</label>
              <input v-model="form.bon.num_ts" type="text"
                     class="w-full bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
            </div>

            <!-- Début -->
            <div>
              <label class="block text-[10px] font-black uppercase tracking-widest text-outline mb-1">Date de début</label>
              <input v-model="form.bon.debut" type="date"
                     class="w-full bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
            </div>

            <!-- Chaînage -->
            <div>
              <label class="block text-[10px] font-black uppercase tracking-widest text-outline mb-1">Chaînage</label>
              <input v-model="form.bon.chainage" type="text"
                     class="w-full bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
            </div>

            <!-- Description -->
            <div class="sm:col-span-2">
              <label class="block text-[10px] font-black uppercase tracking-widest text-outline mb-1">Description</label>
              <textarea v-model="form.bon.description" rows="4"
                        class="w-full bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all resize-none"></textarea>
            </div>

          </div>
        </div>

        <!-- ── Section 2 : Équipements ── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40 flex items-center justify-between">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Équipements <span class="text-outline/50">({{ form.bon.bon_equipements_attributes.length }})</span></h2>
            <button type="button" @click="addEquipement"
                    class="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-primary hover:brightness-125 transition-all">
              <span class="material-symbols-outlined text-sm">add</span>Ajouter
            </button>
          </div>
          <div v-if="form.bon.bon_equipements_attributes.length === 0"
               class="px-6 py-8 text-center text-outline text-sm">
            <span class="material-symbols-outlined text-3xl block mb-2 opacity-30">construction</span>
            Aucun équipement — cliquez sur Ajouter
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-20">Qté</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-20">Hrs</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Équipement</th>
                  <template v-if="date_elements">
                    <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-28">Début</th>
                    <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-28">Fin</th>
                  </template>
                  <th v-if="can_see_costs" class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-24">Taux</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-20">Options</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="(eq, i) in form.bon.bon_equipements_attributes" :key="i"
                    class="hover:bg-surface-container-highest/10">
                  <td class="px-4 py-2">
                    <input v-model="eq.quantite" type="number" step="0.01" min="0"
                           class="w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30" />
                  </td>
                  <td class="px-4 py-2">
                    <input v-model="eq.unite" type="number" step="0.01" min="0"
                           class="w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30" />
                  </td>
                  <td class="px-4 py-2">
                    <select v-model="eq.equipement_id"
                            class="w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30">
                      <option value="">— Sélectionner —</option>
                      <option v-for="e in equipements" :key="e.id" :value="e.id">{{ e.label }}</option>
                    </select>
                  </td>
                  <template v-if="date_elements">
                    <td class="px-4 py-2">
                      <input v-model="eq.debut" type="date"
                             class="w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30" />
                    </td>
                    <td class="px-4 py-2">
                      <input v-model="eq.fin" type="date"
                             class="w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30" />
                    </td>
                  </template>
                  <td v-if="can_see_costs" class="px-4 py-2">
                    <input v-model="eq.taux" type="number" step="0.01" min="0"
                           class="w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30" />
                  </td>
                  <td class="px-4 py-2 text-center">
                    <button type="button" @click="removeEquipement(i)"
                            class="text-outline hover:text-error transition-colors">
                      <span class="material-symbols-outlined text-base">delete</span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── Section 3 : Personnel (type_fonctions) ── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40 flex items-center justify-between">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Personnel <span class="text-outline/50">({{ form.bon.bon_type_fonctions_attributes.length }})</span></h2>
            <button type="button" @click="addTypeFonction"
                    class="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-primary hover:brightness-125 transition-all">
              <span class="material-symbols-outlined text-sm">add</span>Ajouter
            </button>
          </div>
          <div v-if="form.bon.bon_type_fonctions_attributes.length === 0"
               class="px-6 py-8 text-center text-outline text-sm">
            <span class="material-symbols-outlined text-3xl block mb-2 opacity-30">group</span>
            Aucun personnel — cliquez sur Ajouter
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-20">Qté</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-20">Hrs</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Personnel</th>
                  <template v-if="date_elements">
                    <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-28">Début</th>
                    <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-28">Fin</th>
                  </template>
                  <th v-if="can_see_costs" class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-24">Taux</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-20">Options</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="(tf, i) in form.bon.bon_type_fonctions_attributes" :key="i"
                    class="hover:bg-surface-container-highest/10">
                  <td class="px-4 py-2">
                    <input v-model="tf.quantite" type="number" step="0.01" min="0"
                           class="w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30" />
                  </td>
                  <td class="px-4 py-2">
                    <input v-model="tf.unite" type="number" step="0.01" min="0"
                           class="w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30" />
                  </td>
                  <td class="px-4 py-2">
                    <select v-model="tf.type_fonction_id"
                            class="w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30">
                      <option value="">— Sélectionner —</option>
                      <option v-for="t in type_fonctions" :key="t.id" :value="t.id">{{ t.label }}</option>
                    </select>
                  </td>
                  <template v-if="date_elements">
                    <td class="px-4 py-2">
                      <input v-model="tf.debut" type="date"
                             class="w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30" />
                    </td>
                    <td class="px-4 py-2">
                      <input v-model="tf.fin" type="date"
                             class="w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30" />
                    </td>
                  </template>
                  <td v-if="can_see_costs" class="px-4 py-2">
                    <input v-model="tf.taux" type="number" step="0.01" min="0"
                           class="w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30" />
                  </td>
                  <td class="px-4 py-2 text-center">
                    <button type="button" @click="removeTypeFonction(i)"
                            class="text-outline hover:text-error transition-colors">
                      <span class="material-symbols-outlined text-base">delete</span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── Section 4 : Items divers ── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40 flex items-center justify-between">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Matériaux et/ou sous-traitant <span class="text-outline/50">({{ form.bon.bon_items_attributes.length }})</span></h2>
            <button type="button" @click="addItem"
                    class="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-primary hover:brightness-125 transition-all">
              <span class="material-symbols-outlined text-sm">add</span>Ajouter
            </button>
          </div>
          <div v-if="form.bon.bon_items_attributes.length === 0"
               class="px-6 py-8 text-center text-outline text-sm">
            <span class="material-symbols-outlined text-3xl block mb-2 opacity-30">category</span>
            Aucun item — cliquez sur Ajouter
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-20">Qté</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-32">Items / Hrs</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Description</th>
                  <th v-if="can_see_costs" class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-24">Taux</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-20">Options</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="(item, i) in form.bon.bon_items_attributes" :key="i"
                    class="hover:bg-surface-container-highest/10">
                  <td class="px-4 py-2">
                    <input v-model="item.quantite" type="number" step="0.01" min="0"
                           class="w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30" />
                  </td>
                  <td class="px-4 py-2">
                    <input v-model="item.unite" type="number" step="0.01" min="0"
                           class="w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30" />
                  </td>
                  <td class="px-4 py-2">
                    <input v-model="item.description" type="text"
                           class="w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30" />
                  </td>
                  <td v-if="can_see_costs" class="px-4 py-2">
                    <input v-model="item.taux" type="number" step="0.01" min="0"
                           class="w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30" />
                  </td>
                  <td class="px-4 py-2 text-center">
                    <button type="button" @click="removeItem(i)"
                            class="text-outline hover:text-error transition-colors">
                      <span class="material-symbols-outlined text-base">delete</span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── Section 5 : Commentaires ── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Commentaires</h2>
          </div>
          <div class="p-6">
            <textarea v-model="form.bon.commentaires" rows="5"
                      placeholder="Notes, remarques..."
                      class="w-full bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all resize-none"></textarea>
          </div>
        </div>

        <!-- ── Section 6 : Photos ── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Photos</h2>
          </div>
          <div class="p-6 space-y-4">
            <label class="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-outline-variant/30 rounded-xl cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-all group">
              <span class="material-symbols-outlined text-2xl text-outline group-hover:text-primary transition-colors">add_photo_alternate</span>
              <span class="text-sm text-outline group-hover:text-on-surface transition-colors">Parcourir ou glisser des photos...</span>
              <input ref="imageInput" type="file" multiple accept="image/*"
                     class="hidden" @change="onImageChange" />
            </label>
            <div v-if="imagePreviews.length > 0"
                 class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              <div v-for="(img, i) in imagePreviews" :key="i" class="relative group">
                <img :src="img.src" :alt="img.name"
                     class="w-full aspect-square object-cover rounded-lg border border-outline-variant/20" />
                <button type="button" @click="removeImage(i)"
                        class="absolute -top-1.5 -right-1.5 w-5 h-5 bg-error text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
                  <span class="material-symbols-outlined text-[12px]">close</span>
                </button>
                <p class="text-[9px] text-outline mt-1 truncate">{{ img.name }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Actions ── -->
        <div class="flex items-center justify-between pt-2 pb-8">
          <a :href="projet ? `/projets/${projet.id}` : bons_url"
             class="px-5 py-2.5 rounded-md text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container border border-outline-variant/20 hover:bg-surface-container-highest transition-all">
            Annuler
          </a>
          <button
            type="button"
            :disabled="form.processing || !projet"
            @click="submit"
            class="machined-sheen px-8 py-2.5 rounded-md text-on-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="form.processing" class="material-symbols-outlined text-sm animate-spin">progress_activity</span>
            <span v-else class="material-symbols-outlined text-sm">save</span>
            Créer le bon
          </button>
        </div>

      </div>
    </div>
  </AppLayout>
</template>
